import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Calendar } from 'lucide-react';

const CycleCountMetrics = () => {
    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Default to current month YYYY-MM
    const getCurrentMonth = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    };
    const [month, setMonth] = useState(getCurrentMonth());

    useEffect(() => {
        fetchMetrics();
    }, [month]);

    const fetchMetrics = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.inventory.getClientMetrics(month);
            setMetrics(response.data);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch client metrics');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        const mon = d.toLocaleString('en-US', { month: 'short' });
        const day = d.getDate();
        const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
        return (
            <div style={{ lineHeight: '1.2' }}>
                <div>{mon} {day},</div>
                <div style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{time}</div>
            </div>
        );
    };

    return (
        <div className="card">
            <div className="page-header" style={{ marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h3 className="card-title" style={{ margin: 0, fontSize: '1.25rem' }}>Monthly Overview</h3>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                        Cycle count performance aggregated by client.
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f8fafc', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <Calendar size={18} color="var(--primary)" />
                    <input
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: 600, color: 'var(--text-main)', cursor: 'pointer' }}
                    />
                </div>
            </div>

            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}

            <div className="table-container" style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <table className="responsive-table" style={{ minWidth: '900px' }}>
                    <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <tr>
                            <th style={{ padding: '1rem' }}>Client Name</th>
                            <th style={{ padding: '1rem', color: 'var(--text-muted)' }}>Warehouse</th>
                            <th style={{ padding: '1rem' }}>Last Count Date</th>
                            <th style={{ padding: '1rem' }}>Total Counts</th>
                            <th style={{ padding: '1rem' }}>Pending</th>
                            <th style={{ padding: '1rem' }}>Sys Qty</th>
                            <th style={{ padding: '1rem' }}>Phy Qty</th>
                            <th style={{ padding: '1rem' }}>Variance %</th>
                            <th style={{ padding: '1rem' }}>Discrepancy</th>
                            <th style={{ padding: '1rem' }}>Completion</th>
                        </tr>
                    </thead>
                    <tbody className="metrics-table-body">
                        {loading ? (
                            <tr>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                                    <div className="login-spinner" style={{ margin: '0 auto', borderColor: 'var(--primary)', borderTopColor: 'transparent' }}></div>
                                    <div style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>Loading metrics...</div>
                                </td>
                            </tr>
                        ) : metrics.length === 0 ? (
                            <tr>
                                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                                    No cycle count assignments found for this month.
                                </td>
                            </tr>
                        ) : (
                            metrics.map((m, idx) => {
                                const discrepancy = m.total_physical - m.total_system;
                                const variancePct = m.total_system > 0 ? Math.abs((discrepancy / m.total_system) * 100).toFixed(1) : '0.0';
                                const completionPct = m.total_assignments > 0 ? Math.round((m.completed_counts / m.total_assignments) * 100) : 0;

                                return (
                                    <tr key={idx}>
                                        <td style={{ padding: '1rem', fontWeight: 500 }}>{m.client_name}</td>
                                        <td style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>{m.warehouse || '—'}</td>
                                        <td style={{ padding: '1rem' }}>{formatDate(m.last_count_date)}</td>
                                        <td style={{ padding: '1rem' }}>
                                            <div className="badge-oval">
                                                <span className="badge-dot"></span>
                                                {m.total_assignments}
                                            </div>
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            {m.pending_counts > 0 ? (
                                                <div className="badge-square-danger">{m.pending_counts}</div>
                                            ) : (
                                                <div className="badge-square-success">0</div>
                                            )}
                                        </td>
                                        <td style={{ padding: '1rem', color: 'var(--text-muted)', fontWeight: 500 }}>{m.total_system || 0}</td>
                                        <td style={{ padding: '1rem', color: 'var(--text-main)', fontWeight: 600 }}>{m.total_physical || 0}</td>
                                        <td style={{ padding: '1rem', fontWeight: 500 }}>{variancePct}%</td>
                                        <td style={{ padding: '1rem' }}>
                                            {discrepancy > 0 ? (
                                                <div className="badge-pill-danger" style={{ background: '#e11d48' }}>+{discrepancy}</div>
                                            ) : discrepancy < 0 ? (
                                                <div className="badge-pill-danger" style={{ background: '#f59e0b' }}>{discrepancy}</div>
                                            ) : m.completed_counts > 0 ? (
                                                <div className="badge-pill-success">OK</div>
                                            ) : (
                                                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>-</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            <div className="metrics-progress-container">
                                                <div className="metrics-progress-bar">
                                                    <div
                                                        className="metrics-progress-fill"
                                                        style={{
                                                            width: `${completionPct}%`,
                                                            background: completionPct === 100 ? '#10b981' : (completionPct > 0 ? '#3b82f6' : 'transparent')
                                                        }}
                                                    ></div>
                                                </div>
                                                <div className="metrics-progress-text">{completionPct}%</div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CycleCountMetrics;
